// src/utils/versionManager.ts

/**
 * Version Manager for TWA/PWA Updates
 *
 * Handles automatic app updates by:
 * 1. Comparing current version with cached version
 * 2. Clearing service worker caches when version changes
 * 3. Force reloading to get the latest app code
 * 4. Preventing reload loops with safety flags
 *
 * IMPORTANT: This only clears app caches (JS, CSS, HTML).
 * User data in localStorage (bills, settings) is NOT affected.
 */

import { version as APP_VERSION } from '../../package.json';

// localStorage keys
const STORAGE_KEYS = {
	CACHED_VERSION: 'bill-tracker-app-version',
	UPDATE_IN_PROGRESS: 'bill-tracker-update-in-progress',
	JUST_UPDATED: 'bill-tracker-just-updated',
	UPDATE_ATTEMPTS: 'bill-tracker-update-attempts',
} as const;

// Maximum update attempts before giving up (prevents infinite loops)
const MAX_UPDATE_ATTEMPTS = 3;

export interface VersionCheckResult {
	currentVersion: string;
	cachedVersion: string | null;
	needsUpdate: boolean;
	isFirstRun: boolean;
	justUpdated: boolean;
}

/**
 * Get the current app version from package.json
 */
export function getAppVersion(): string {
	return APP_VERSION;
}

/**
 * Get the cached version from localStorage
 */
export function getCachedVersion(): string | null {
	try {
		return localStorage.getItem(STORAGE_KEYS.CACHED_VERSION);
	} catch {
		return null;
	}
}

/**
 * Store the current version in localStorage
 */
export function setCachedVersion(version: string): void {
	try {
		localStorage.setItem(STORAGE_KEYS.CACHED_VERSION, version);
	} catch {
		console.warn('Failed to cache version in localStorage');
	}
}

/**
 * Check if we just completed an update
 */
export function checkJustUpdated(): boolean {
	try {
		const justUpdated = localStorage.getItem(STORAGE_KEYS.JUST_UPDATED);
		if (justUpdated === 'true') {
			// Clear the flag after reading
			localStorage.removeItem(STORAGE_KEYS.JUST_UPDATED);
			localStorage.removeItem(STORAGE_KEYS.UPDATE_ATTEMPTS);
			return true;
		}
		return false;
	} catch {
		return false;
	}
}

/**
 * Set flag indicating update just completed
 */
function setJustUpdatedFlag(): void {
	try {
		localStorage.setItem(STORAGE_KEYS.JUST_UPDATED, 'true');
	} catch {
		// Ignore
	}
}

/**
 * Check if an update is currently in progress (prevents reload loops)
 */
function isUpdateInProgress(): boolean {
	try {
		return localStorage.getItem(STORAGE_KEYS.UPDATE_IN_PROGRESS) === 'true';
	} catch {
		return false;
	}
}

/**
 * Set/clear the update in progress flag
 */
function setUpdateInProgress(inProgress: boolean): void {
	try {
		if (inProgress) {
			localStorage.setItem(STORAGE_KEYS.UPDATE_IN_PROGRESS, 'true');
		} else {
			localStorage.removeItem(STORAGE_KEYS.UPDATE_IN_PROGRESS);
		}
	} catch {
		// Ignore
	}
}

/**
 * Get current update attempt count
 */
function getUpdateAttempts(): number {
	try {
		const attempts = localStorage.getItem(STORAGE_KEYS.UPDATE_ATTEMPTS);
		return attempts ? parseInt(attempts, 10) : 0;
	} catch {
		return 0;
	}
}

/**
 * Increment update attempt counter
 */
function incrementUpdateAttempts(): number {
	try {
		const current = getUpdateAttempts();
		const next = current + 1;
		localStorage.setItem(STORAGE_KEYS.UPDATE_ATTEMPTS, next.toString());
		return next;
	} catch {
		return MAX_UPDATE_ATTEMPTS; // Fail safe - stop trying
	}
}

/**
 * Clear all service worker caches
 * This removes cached JS, CSS, HTML, fonts, etc.
 * Does NOT affect localStorage (user data is safe)
 */
async function clearAllCaches(): Promise<void> {
	try {
		// Get all cache names and delete them
		if ('caches' in window) {
			const cacheNames = await caches.keys();
			await Promise.all(
				cacheNames.map((cacheName) => {
					console.log(`Clearing cache: ${cacheName}`);
					return caches.delete(cacheName);
				})
			);
			console.log('All caches cleared successfully');
		}
	} catch (error) {
		console.warn('Failed to clear caches:', error);
	}
}

/**
 * Unregister all service workers
 */
async function unregisterServiceWorkers(): Promise<void> {
	try {
		if ('serviceWorker' in navigator) {
			const registrations = await navigator.serviceWorker.getRegistrations();
			await Promise.all(
				registrations.map((registration) => {
					console.log('Unregistering service worker:', registration.scope);
					return registration.unregister();
				})
			);
			console.log('All service workers unregistered');
		}
	} catch (error) {
		console.warn('Failed to unregister service workers:', error);
	}
}

/**
 * Force a hard reload of the page
 * Uses multiple strategies to ensure fresh content
 */
function forceHardReload(): void {
	// Set flag to show notification after reload
	setJustUpdatedFlag();

	// Clear the in-progress flag before reload
	// (it will be set again if update fails)
	setUpdateInProgress(false);

	// Strategy 1: Try location.reload(true) for hard reload
	// Note: The 'true' parameter is deprecated but still works in some browsers
	try {
		// Use a slight delay to ensure localStorage writes complete
		setTimeout(() => {
			// Add cache-busting query param to force fresh load
			const url = new URL(window.location.href);
			url.searchParams.set('_v', Date.now().toString());
			window.location.href = url.toString();
		}, 100);
	} catch {
		// Fallback: standard reload
		window.location.reload();
	}
}

/**
 * Perform the full update process
 * 1. Clear all caches
 * 2. Unregister service workers
 * 3. Update stored version
 * 4. Force hard reload
 */
export async function performUpdate(): Promise<void> {
	console.log('Starting app update process...');

	// Check for too many attempts (loop prevention)
	const attempts = incrementUpdateAttempts();
	if (attempts > MAX_UPDATE_ATTEMPTS) {
		console.error('Too many update attempts, aborting to prevent loop');
		setUpdateInProgress(false);
		localStorage.removeItem(STORAGE_KEYS.UPDATE_ATTEMPTS);
		return;
	}

	// Set in-progress flag
	setUpdateInProgress(true);

	try {
		// Clear caches and unregister service workers in parallel
		await Promise.all([
			clearAllCaches(),
			unregisterServiceWorkers(),
		]);

		// Update the cached version
		setCachedVersion(APP_VERSION);

		console.log(`Update complete. New version: ${APP_VERSION}`);

		// Force reload to get fresh content
		forceHardReload();
	} catch (error) {
		console.error('Update failed:', error);
		setUpdateInProgress(false);
	}
}

/**
 * Check if app needs an update
 * Returns detailed status without performing the update
 */
export function checkVersion(): VersionCheckResult {
	const currentVersion = APP_VERSION;
	const cachedVersion = getCachedVersion();
	const isFirstRun = cachedVersion === null;
	const justUpdated = checkJustUpdated();

	// Determine if update is needed
	let needsUpdate = false;

	if (!isFirstRun && cachedVersion !== currentVersion) {
		// Version mismatch - need to update
		// But check if we're already in an update loop
		if (!isUpdateInProgress() && getUpdateAttempts() < MAX_UPDATE_ATTEMPTS) {
			needsUpdate = true;
		}
	}

	// First run - just store the version, no update needed
	if (isFirstRun) {
		setCachedVersion(currentVersion);
	}

	return {
		currentVersion,
		cachedVersion,
		needsUpdate,
		isFirstRun,
		justUpdated,
	};
}

/**
 * Manual update check - for "Check for Updates" button
 * Forces a cache clear and reload regardless of version
 */
export async function forceCheckForUpdates(): Promise<void> {
	console.log('Manual update check triggered');

	// Reset attempt counter for manual checks
	localStorage.removeItem(STORAGE_KEYS.UPDATE_ATTEMPTS);

	await performUpdate();
}

/**
 * Initialize version checking on app startup
 * Call this once when the app loads
 */
export async function initVersionCheck(): Promise<VersionCheckResult> {
	const result = checkVersion();

	console.log('Version check:', {
		current: result.currentVersion,
		cached: result.cachedVersion,
		needsUpdate: result.needsUpdate,
		isFirstRun: result.isFirstRun,
		justUpdated: result.justUpdated,
	});

	// If update is needed, perform it automatically
	if (result.needsUpdate) {
		console.log('Version mismatch detected, initiating update...');
		await performUpdate();
	}

	// Clear any stale in-progress flags if we got here without updating
	if (!result.needsUpdate) {
		setUpdateInProgress(false);
		localStorage.removeItem(STORAGE_KEYS.UPDATE_ATTEMPTS);
	}

	return result;
}
